import {
	Badge,
	Box,
	CircularProgress,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import React from "react";
import { useAirtableStatus } from "@gui/hooks/useAirtableStatus";
import { AppVersion } from "@gui/widgets/AppVersion";

import LogoutIcon from "@mui/icons-material/Logout";
import InfoIcon from "@mui/icons-material/Info";
import FeedbackIcon from "@mui/icons-material/Feedback";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { NotInterested } from "@mui/icons-material";

import "./DrawerNavigation.css";
import { useDrawerNavigationViewModel, type TopItem } from "./DrawerNavigation.vm";
import { type NavLink } from "./navLinks";
export type { NavLink } from "./navLinks";

export const SIDEBAR_COLLAPSED_WIDTH = 60;
const MORE_BUTTON_HEIGHT = 48;

const ItemBreak = React.memo(function ItemBreak() {
	return <ListItem className="sidebar-item-break" />;
});

const STATUS_COLORS: Record<string, string> = {
	none: "#4caf50",
	minor: "#ff9800",
	major: "#f44336",
	critical: "#f44336",
};

function AirtableStatusIndicator({ expanded }: { expanded: boolean }) {
	const { indicator } = useAirtableStatus();
	const color = indicator ? STATUS_COLORS[indicator] : "#9e9e9e";

	return (
		<Tooltip title={expanded ? "" : "Airtable"} placement="right">
			<ListItemButton onClick={() => window.open("https://status.airtable.com", "_blank", "noopener,noreferrer")}>
				<ListItemIcon>
					<Box className="sidebar-status-icon">
						<Box className="sidebar-status-dot" sx={{ backgroundColor: color }} />
					</Box>
				</ListItemIcon>
				<ListItemText primary="Airtable Status" />
			</ListItemButton>
		</Tooltip>
	);
}

interface DrawerNavigationProps {
	onNavigate: (link: NavLink) => void;
	mobileOpen: boolean;
	onMobileClose: () => void;
}

export const DrawerNavigation = (props: DrawerNavigationProps) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [expanded, setExpanded] = React.useState(false);
	const [moreAnchor, setMoreAnchor] = React.useState<HTMLElement | null>(null);
	const [overflowIndex, setOverflowIndex] = React.useState<number | null>(null);

	const contentRef = React.useRef<HTMLDivElement>(null);
	const ghostRef = React.useRef<HTMLUListElement>(null);
	const bottomRef = React.useRef<HTMLDivElement>(null);

	const closeMenusOnNav = React.useCallback(() => {
		if (isMobile) props.onMobileClose();
		setMoreAnchor(null);
		setExpanded(false);
	}, [isMobile, props]);

	const vm = useDrawerNavigationViewModel({
		onNavigate: props.onNavigate,
		mobileOpen: props.mobileOpen,
		onMobileClose: props.onMobileClose,
		isMobile,
		closeMenusOnNav,
	});

	React.useLayoutEffect(() => {
		if (isMobile) {
			setOverflowIndex(null);
			return;
		}
		const paperEl = contentRef.current?.closest(".MuiDrawer-paper") as HTMLElement | null;
		const ghost = ghostRef.current;
		const bottom = bottomRef.current;
		if (!paperEl || !ghost || !bottom) return;

		const compute = () => {
			const available = paperEl.clientHeight - bottom.offsetHeight;
			if (available <= 0) return;

			if (ghost.scrollHeight <= available) {
				setOverflowIndex(null);
				return;
			}

			const budget = available - MORE_BUTTON_HEIGHT;
			const children = Array.from(ghost.children) as HTMLElement[];
			const ghostTop = ghost.offsetTop;
			let cutoff = children.length;
			for (let i = 0; i < children.length; i++) {
				const child = children[i];
				const bottomEdge = child.offsetTop + child.offsetHeight - ghostTop;
				if (bottomEdge > budget) {
					cutoff = i;
					break;
				}
			}
			while (cutoff > 0 && vm.topItems[cutoff - 1]?.kind === "divider") cutoff--;
			setOverflowIndex(cutoff);
		};

		compute();
		const ro = new ResizeObserver(compute);
		ro.observe(paperEl);
		ro.observe(bottom);
		return () => ro.disconnect();
	}, [isMobile, vm.topItems]);

	const showExpanded = isMobile || expanded;
	const drawerClass = `sidebar-drawer${showExpanded ? " expanded" : ""}`;

	const renderItem = (item: TopItem, inMenu: boolean): React.ReactNode => {
		if (item.kind === "divider") {
			return inMenu ? null : <ItemBreak key={item.key} />;
		}

		const onClick = item.kind === "nav" ? () => vm.handleNavClick(item.link) : vm.handleLogout;
		const rawIcon = item.kind === "nav" ? item.link.icon : <LogoutIcon />;
		const label = item.kind === "nav" ? item.link.text : "Logout";

		// Decorate the icon with an issue-count badge for any nav row whose path has a fetcher
		// in IssueCountsContext. Badge shows a CircularProgress while the initial fetch is in
		// flight (count still undefined), the number when > 0, and is hidden when the count is
		// known to be 0 — Badge with badgeContent={0} self-hides via showZero defaulting to false.
		const navPath = item.kind === "nav" ? item.link.to : undefined;
		const badge = vm.getItemBadgeState(navPath);
		const icon = badge.isFetching ? (
			<Badge
				badgeContent={<CircularProgress size={10} thickness={6} sx={{ color: "inherit" }} />}
				color="error"
				overlap="circular"
			>
				{rawIcon}
			</Badge>
		) : badge.showCountBadge ? (
			<Badge badgeContent={badge.count} color="error" overlap="circular">
				{rawIcon}
			</Badge>
		) : (
			rawIcon
		);

		if (inMenu) {
			return (
				<MenuItem key={item.key} onClick={onClick}>
					<ListItemIcon>{icon}</ListItemIcon>
					<ListItemText primary={label} />
				</MenuItem>
			);
		}

		return (
			<ListItem key={item.key} disablePadding>
				<Tooltip title={showExpanded ? "" : label} placement="right">
					<ListItemButton onClick={onClick}>
						<ListItemIcon>{icon}</ListItemIcon>
						<ListItemText primary={label} />
					</ListItemButton>
				</Tooltip>
			</ListItem>
		);
	};

	const visibleTopItems = overflowIndex === null ? vm.topItems : vm.topItems.slice(0, overflowIndex);
	const overflowedItems = overflowIndex === null ? [] : vm.topItems.slice(overflowIndex);

	const navList = (
		<Box ref={contentRef} className="sidebar-content">
			<List className="sidebar-top-list">
				{visibleTopItems.map((item) => renderItem(item, false))}
				{overflowedItems.length > 0 && (
					<ListItem key="more" disablePadding>
						<Tooltip title={showExpanded ? "" : "More"} placement="right">
							<ListItemButton onClick={(e) => setMoreAnchor(e.currentTarget)}>
								<ListItemIcon>
									<MoreHorizIcon />
								</ListItemIcon>
								<ListItemText primary="More" />
							</ListItemButton>
						</Tooltip>
					</ListItem>
				)}
			</List>
			{!isMobile && (
				<List ref={ghostRef} className="sidebar-ghost-list" aria-hidden>
					{vm.topItems.map((item) => renderItem(item, false))}
				</List>
			)}
			<Box ref={bottomRef} className="sidebar-bottom-block">
				<List>
					{vm.user?.isAdmin && (
						<>
							<ListItem key="clear-cache" disablePadding>
								<Tooltip title={showExpanded ? "" : "Clear Cache"} placement="right">
									<ListItemButton onClick={vm.handleClearCache}>
										<ListItemIcon>
											<NotInterested />
										</ListItemIcon>
										<ListItemText primary="Clear Portal Cache" />
									</ListItemButton>
								</Tooltip>
							</ListItem>
							<ListItem key="airtable-status" disablePadding>
								<AirtableStatusIndicator expanded={showExpanded} />
							</ListItem>
						</>
					)}
					<ListItem key="feedback" disablePadding>
						<Tooltip title={showExpanded ? "" : "Feedback"} placement="right">
							<ListItemButton onClick={vm.handleOpenFeedback}>
								<ListItemIcon>
									<FeedbackIcon />
								</ListItemIcon>
								<ListItemText primary="Feedback" />
							</ListItemButton>
						</Tooltip>
					</ListItem>
					<ListItem key="version" className="sidebar-version">
						<ListItemIcon>
							<InfoIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={<AppVersion />} />
					</ListItem>
				</List>
			</Box>
			<Menu
				anchorEl={moreAnchor}
				open={Boolean(moreAnchor)}
				onClose={() => setMoreAnchor(null)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				{overflowedItems.map((item) => renderItem(item, true))}
			</Menu>
		</Box>
	);

	if (isMobile) {
		return (
			<Drawer
				variant="temporary"
				anchor="left"
				open={props.mobileOpen}
				onClose={props.onMobileClose}
				className="sidebar-drawer expanded sidebar-drawer-mobile"
			>
				{navList}
			</Drawer>
		);
	}

	return (
		<Drawer
			variant="permanent"
			anchor="left"
			className={drawerClass}
			onMouseEnter={() => setExpanded(true)}
			onMouseLeave={() => setExpanded(false)}
		>
			{navList}
		</Drawer>
	);
};
